import { ComponentProps, forwardRef, useEffect, useState } from "react";
import classNames from "classnames";
import s from "./InputField.module.css";

export type Props = ComponentProps<'input'> & {
    isError?: boolean;
    labelClassName?: string;
    formatter?: (value: string) => string;
    isReset?: boolean;
    useValueState?: boolean;
    color?: "purple" | "gray";
    error?: string;
    isDisabled?: boolean;
    defaultValue?: string | number;
    disableAutofill?: boolean;
    readonly?: boolean;
    studio?: boolean;
};

export const InputField = forwardRef<HTMLInputElement, Props>(({
    labelClassName,
    className,
    isError = false,
    onFocus,
    onBlur,
    formatter,
    defaultValue = '',
    isReset = false,
    useValueState = true,
    color = "purple",
    error,
    isDisabled,
    onChange,
    readOnly,
    disableAutofill = false,
    value: externalValue,
    studio,
    ...restProps
}, ref) => {
    const [isFocus, setIsFocus] = useState(false);
    const [internalValue, setInternalValue] = useState(defaultValue);

    const value = useValueState ? internalValue : externalValue ?? defaultValue;
    const stringValue = String(value);

    useEffect(() => {
        if (externalValue !== undefined) {
            setInternalValue(String(externalValue));
        }
    }, [externalValue]);

    useEffect(() => {
        if (isReset) {
            setInternalValue('');
        }
    }, [isReset]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        const formattedValue = formatter ? formatter(newValue) : newValue;

        if (useValueState) {
            setInternalValue(formattedValue);
        }

        onChange?.({
            ...event,
            target: {
                ...event.target,
                value: formattedValue
            }
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (readOnly) {
            e.preventDefault();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        if (disableAutofill) {
            e.preventDefault();
        }
    };

    return (
        <label className={classNames(s['input-field'], labelClassName)}>
            <input
                {...restProps}
                autoComplete={disableAutofill ? "off" : restProps.autoComplete}
                ref={ref}
                value={useValueState ? value : defaultValue}
                className={classNames(
                    s['input-field__input'],
                    s[`input-field__input_${color}`],
                    { [s['input-field__input_error']]: isError },
                    { [s['input-field__input_focus']]: isFocus },
                    { [s['input-field__input_disabled']]: isDisabled },
                    { [s['input-field__input_studio']]: studio },
                    className
                )}
                readOnly={readOnly || isDisabled}
                onFocus={(event) => {
                    setIsFocus(true);
                    onFocus?.(event);
                }}
                onBlur={(event) => {
                    setIsFocus(false);
                    onBlur?.(event);
                }}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={disableAutofill ? handlePaste : undefined}
            />

            {error && (
                <div className={s['input-field__error']}>
                    {error}
                </div>
            )}

            {restProps.maxLength && (
                <div className={s['input-field__counter']}>
                    {stringValue.length}/{restProps.maxLength}
                </div>
            )}
        </label>
    );
});
